import { useEffect, useRef, useState } from 'react';

type WakeLockSentinel = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', cb: () => void) => void;
};

type WakeLockNavigator = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinel>;
  };
};

export function useWakeLock(enabled: boolean) {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const nav = navigator as WakeLockNavigator;
    if (!nav.wakeLock) {
      setError('Wake Lock non supportato su questo browser');
      return;
    }

    let cancelled = false;

    async function acquire() {
      try {
        const sentinel = await nav.wakeLock!.request('screen');
        if (cancelled) {
          await sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
        sentinel.addEventListener('release', () => setActive(false));
        setActive(true);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'wake lock fallito');
      }
    }

    acquire();

    function handleVisibility() {
      if (document.visibilityState === 'visible' && !sentinelRef.current?.released) {
        // pagina tornata visibile, rinnovo
        acquire();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      if (sentinelRef.current && !sentinelRef.current.released) {
        sentinelRef.current.release().catch(() => {});
      }
      sentinelRef.current = null;
      setActive(false);
    };
  }, [enabled]);

  return { active, error };
}
